import React from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip } from 'antd';
import { Info, ListChecks } from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import COLORS from '../../util/constants/colors';
import { humanizeSnakeCase } from './helpers';

export const UNKNOWN_OUTCOME = 'unknown';

const UNCLASSIFIED_OUTCOMES = [UNKNOWN_OUTCOME, 'audio_or_transcript_issue'];
const OUTCOME_NUMERIC_COLUMN_WIDTH = 72;
const DROPPED_TRANSFER_COLUMN_WIDTH = 104;
const OUTCOME_TABLE_SCROLL = { x: 420, y: 188 };

export const isUnclassifiedOutcome = (outcome) => UNCLASSIFIED_OUTCOMES.includes(outcome);

const objectiveBreakdownColumns = [
  {
    title: 'Caller Objective',
    dataIndex: 'key',
    key: 'key',
    // humanizeSnakeCase('unknown') already yields 'Unknown'.
    render: humanizeSnakeCase,
  },
  {
    title: 'Calls',
    dataIndex: 'count',
    key: 'count',
    width: OUTCOME_NUMERIC_COLUMN_WIDTH,
  },
  {
    title: 'Share',
    dataIndex: 'percentage',
    key: 'percentage',
    width: OUTCOME_NUMERIC_COLUMN_WIDTH,
    render: (value) => `${value || 0}%`,
  },
];

const isExpandableTransferRow = (row) => Boolean(row.is_transfer) && (row.objective_breakdown || []).length > 0;

export const outcomeDistributionTooltip = (taxonomy) => (
  <div className="maira-taxonomy-tooltip">
    <div className="maira-taxonomy-tooltip-header">
      Outcome taxonomy v
      {taxonomy?.version || '-'}
    </div>
    <div className="maira-taxonomy-tooltip-list">
      {(taxonomy?.outcomes || []).map((outcome) => (
        <div className="maira-taxonomy-tooltip-item" key={outcome.label}>
          <span className="maira-taxonomy-tooltip-label">
            {outcome.display_label || outcome.label}
          </span>
          <span className="maira-taxonomy-tooltip-description">
            {outcome.description}
          </span>
        </div>
      ))}
    </div>
    <div className="maira-taxonomy-tooltip-note">
      Unknown is shown when a call has no classifier outcome. Audio or transcript issues count toward coverage gaps, not abandonment.
    </div>
  </div>
);

const MairaOutcomes = ({
  outcomes,
  taxonomy,
  loading,
  onOutcomeClick,
  onTransferDestinationClick,
  onTransferObjectiveClick,
}) => {
  const rows = outcomes?.distribution || [];
  const showTaxonomyTooltip = (taxonomy?.outcomes || []).length > 0;
  // Only attach `expandable` when a row actually expands. In antd 4 / rc-table,
  // any expandedRowRender inserts an empty expand-icon column before consulting
  // rowExpandable — so passing it in old mode (or when no clinic transfers
  // exist) would add a stray empty column.
  const hasExpandableRows = rows.some(isExpandableTransferRow);
  const showDroppedTransfersColumn = rows.some((row) => Object.prototype.hasOwnProperty.call(row, 'dropped_transfer_count'));

  // Only outcomes the report endpoint accepts are clickable: current taxonomy
  // labels plus the synthetic "unknown" bucket. A distribution row whose raw
  // call_outcome falls outside the taxonomy (e.g. a value persisted before a
  // taxonomy change) would otherwise return HTTP 400 on click. This mirrors the
  // backend allow-list in MairaAnalyticsService#normalize_outcome.
  const clickableOutcomes = new Set([
    ...(taxonomy?.outcomes || []).map((outcome) => outcome.label),
    UNKNOWN_OUTCOME,
  ]);
  const isClickableOutcome = (outcome) => clickableOutcomes.has(outcome);

  // Closure (not the module-level helper) so each objective sub-row can drill
  // into its parent transfer destination plus its own objective key. Stop
  // propagation so the inner-table row click never bubbles to the outer
  // destination row's onClick.
  const renderObjectiveBreakdown = (row) => (
    <Table
      className="maira-objective-breakdown"
      rowKey="key"
      columns={objectiveBreakdownColumns}
      dataSource={row.objective_breakdown || []}
      pagination={false}
      rowClassName={() => 'maira-outcome-row-clickable'}
      onRow={(sub) => ({
        onClick: (event) => {
          event.stopPropagation();
          onTransferObjectiveClick(row.destination, sub.key);
        },
      })}
    />
  );

  const columns = [
    {
      title: 'Outcome',
      dataIndex: 'display_label',
      key: 'display_label',
      render: (value, row) => (
        <div className="maira-outcome-label-cell">
          <span>{value || 'Unknown'}</span>
          {isUnclassifiedOutcome(row.outcome) && (
            <span className="maira-outcome-unclassified-tag">
              Unclassified
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Calls',
      dataIndex: 'count',
      key: 'count',
      width: OUTCOME_NUMERIC_COLUMN_WIDTH,
    },
    {
      title: 'Share',
      dataIndex: 'percentage',
      key: 'percentage',
      width: OUTCOME_NUMERIC_COLUMN_WIDTH,
      render: (value) => `${value || 0}%`,
    },
  ];

  if (showDroppedTransfersColumn) {
    columns.push({
      title: (
        <>
          Dropped
          {' '}
          <br />
          Transfers
        </>
      ),
      dataIndex: 'dropped_transfer_count',
      key: 'dropped_transfer_count',
      width: DROPPED_TRANSFER_COLUMN_WIDTH,
      render: (value, row) => (row.is_transfer ? value || 0 : ''),
    });
  }

  return (
    <div className="maira-panel maira-outcomes">
      <div className="maira-panel-title">
        <Typography variant="text" size="medium" bold>
          <ListChecks color={COLORS.BLUE_PRIMARY_LITE} size={16} weight="bold" />
          &nbsp;
          Outcome Distribution
        </Typography>
        {showTaxonomyTooltip && (
          <Tooltip
            overlayClassName="maira-taxonomy-tooltip-overlay"
            placement="right"
            title={outcomeDistributionTooltip(taxonomy)}
          >
            <button
              type="button"
              className="maira-outcome-info"
              aria-label="View outcome distribution details"
            >
              <Info color={COLORS.TEXT_TERTIARY_LITE} size={16} weight="bold" />
            </button>
          </Tooltip>
        )}
      </div>
      <Table
        rowKey="outcome"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        rowClassName={(row) => [
          (row.is_transfer || isClickableOutcome(row.outcome)) ? 'maira-outcome-row-clickable' : '',
          isUnclassifiedOutcome(row.outcome) ? 'maira-outcome-row-unclassified' : '',
        ].filter(Boolean).join(' ')}
        onRow={(row) => {
          if (row.is_transfer) {
            return { onClick: () => onTransferDestinationClick(row.destination) };
          }

          return isClickableOutcome(row.outcome)
            ? { onClick: () => onOutcomeClick(row.outcome) }
            : {};
        }}
        tableLayout="fixed"
        scroll={OUTCOME_TABLE_SCROLL}
        expandable={hasExpandableRows ? {
          rowExpandable: isExpandableTransferRow,
          expandedRowRender: renderObjectiveBreakdown,
        } : undefined}
      />
    </div>
  );
};

MairaOutcomes.propTypes = {
  outcomes: PropTypes.object,
  taxonomy: PropTypes.object,
  loading: PropTypes.bool,
  onOutcomeClick: PropTypes.func,
  onTransferDestinationClick: PropTypes.func,
  onTransferObjectiveClick: PropTypes.func,
};

MairaOutcomes.defaultProps = {
  outcomes: {},
  taxonomy: {},
  loading: false,
  onOutcomeClick: () => {},
  onTransferDestinationClick: () => {},
  onTransferObjectiveClick: () => {},
};

export default MairaOutcomes;
export { UNCLASSIFIED_OUTCOMES };
